import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../components/ConfirmDialog";

type Tab = "users" | "devices" | "sessions";

interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  role: string;
  status: string;
  created_at: string;
}
interface AdminDevice {
  id: string;
  hostname: string;
  os: string;
  access_code: string | null;
  online: boolean;
  claimants: number;
  created_at: string;
}
interface AdminSession {
  id: string;
  mode: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  initiator_email: string;
  initiator_name: string;
  device_hostname: string;
  device_code: string | null;
}
interface UserDetail {
  user: AdminUser & { updated_at: string };
  devices: {
    id: string;
    hostname: string;
    os: string;
    access_code: string | null;
    created_at: string;
    last_seen_at: string | null;
    claimants: number;
  }[];
  sessions: {
    id: string;
    mode: string;
    status: string;
    started_at: string;
    ended_at: string | null;
    device_hostname: string;
    device_code: string | null;
  }[];
  counts: { devices: number; sessions: number };
}

const TABS: { key: Tab; label: string }[] = [
  { key: "users", label: "Users" },
  { key: "devices", label: "Machines" },
  { key: "sessions", label: "Connections" }
];

function fmt(ts: string | null): string {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export function AdminPage() {
  const { me } = useAuth();
  const confirm = useConfirm();
  const [tab, setTab] = useState<Tab>("users");
  const [err, setErr] = useState<string>("");

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [devices, setDevices] = useState<AdminDevice[]>([]);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [sessionScope, setSessionScope] = useState<"active" | "all">("active");
  const [busy, setBusy] = useState(false);

  // User details drawer + delete flow.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [purgingId, setPurgingId] = useState<string | null>(null);
  // Only close the modal when a press *starts* on the backdrop — a drag-select
  // that ends on the backdrop shouldn't dismiss it.
  const backdropDown = useRef(false);

  const load = useCallback(async () => {
    setErr("");
    setBusy(true);
    try {
      if (tab === "users") setUsers(await api<AdminUser[]>("/v1/admin/users"));
      else if (tab === "devices") setDevices(await api<AdminDevice[]>("/v1/admin/devices"));
      else setSessions(await api<AdminSession[]>(`/v1/admin/sessions?status=${sessionScope}`));
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status} ${e.code}` : String(e));
    } finally {
      setBusy(false);
    }
  }, [tab, sessionScope]);

  useEffect(() => {
    load();
  }, [load]);

  // Returns null on success, or an error string on failure (so callers can
  // route it to the right banner — the page banner is hidden behind the modal).
  const patchUser = useCallback(
    async (id: string, body: { role?: string; status?: string }): Promise<string | null> => {
      try {
        await api(`/v1/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) });
        await load();
        return null;
      } catch (e) {
        const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
        setErr(msg);
        return msg;
      }
    },
    [load]
  );

  const openDetails = useCallback(async (id: string) => {
    setDetailId(id);
    setDetail(null);
    setDetailErr("");
    setDetailBusy(true);
    try {
      setDetail(await api<UserDetail>(`/v1/admin/users/${id}`));
    } catch (e) {
      setDetailErr(e instanceof ApiError ? `${e.status} ${e.code}` : String(e));
    } finally {
      setDetailBusy(false);
    }
  }, []);

  // Returns null on success, or an error string on failure.
  const removeUser = useCallback(
    async (u: Pick<AdminUser, "id" | "email" | "display_name">): Promise<string | null> => {
      const ok = await confirm({
        title: `Remove ${u.display_name}?`,
        message:
          `${u.email}\n\n` +
          `They lose access immediately, any live connection is ended, and the ` +
          `account is hidden from this list. Their machines and history are kept.`,
        confirmLabel: "Remove",
        danger: true
      });
      if (!ok) return null;
      setRemovingId(u.id);
      try {
        await api(`/v1/admin/users/${u.id}`, { method: "DELETE" });
        setDetailId(null);
        setDetail(null);
        await load();
        return null;
      } catch (e) {
        const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
        setErr(msg);
        return msg;
      } finally {
        setRemovingId(null);
      }
    },
    [load, confirm]
  );

  // Hard-delete a previously removed account: the row is erased (their machines
  // included) and the email becomes available for a brand-new registration.
  const purgeUser = useCallback(
    async (u: Pick<AdminUser, "id" | "email" | "display_name">) => {
      const ok = await confirm({
        title: `Permanently delete ${u.display_name}?`,
        message:
          `${u.email}\n\n` +
          `This erases the account and the machines it owns — including session ` +
          `history other users have with those machines. The email can then ` +
          `register a brand-new account.\n\nThis cannot be undone.`,
        confirmLabel: "Delete permanently",
        danger: true
      });
      if (!ok) return;
      setPurgingId(u.id);
      try {
        await api(`/v1/admin/users/${u.id}/purge`, { method: "DELETE" });
        await load();
      } catch (e) {
        setErr(e instanceof ApiError ? `${e.status} ${e.code}` : String(e));
      } finally {
        setPurgingId(null);
      }
    },
    [load, confirm]
  );

  const terminate = useCallback(
    async (id: string) => {
      try {
        await api(`/v1/admin/sessions/${id}/terminate`, { method: "POST", body: "{}" });
        await load();
      } catch (e) {
        setErr(e instanceof ApiError ? `${e.status} ${e.code}` : String(e));
      }
    },
    [load]
  );

  // Removed (soft-deleted) accounts get their own section: they can't sign in
  // or re-register, and from here the admin can Restore or Delete permanently.
  const activeUsers = users.filter((u) => u.status !== "deleted");
  const removedUsers = users.filter((u) => u.status === "deleted");

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin</h1>
          <p className="text-sm text-white/50">Manage users, machines and live connections.</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="px-3 py-2 rounded-lg text-sm bg-white/5 hover:bg-white/10 text-white/80"
        >
          ↻ Refresh
        </button>
      </div>

      <div className="flex gap-1 mb-4 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition ${
              tab === t.key
                ? "border-accent text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {err && (
        <div className="mb-4 px-4 py-2 rounded-lg bg-red-500/10 text-red-300 text-sm">{err}</div>
      )}
      {busy && <div className="text-white/40 text-sm mb-2">Loading…</div>}

      {tab === "users" && (
        <>
        <div className="glass overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-white/50 text-left">
              <tr className="border-b border-white/10">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.map((u) => {
                const isSelf = u.id === me?.id;
                const isAdmin = u.role === "admin" || u.role === "owner";
                return (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openDetails(u.id)}
                        className="text-left group"
                      >
                        <div className="text-white font-medium group-hover:text-accent">
                          {u.display_name}
                        </div>
                        <div className="text-white/40 text-xs">{u.email}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          isAdmin ? "bg-purple-500/20 text-purple-200" : "bg-white/10 text-white/70"
                        }`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          u.status === "active"
                            ? "bg-green-500/20 text-green-300"
                            : "bg-red-500/20 text-red-300"
                        }`}
                      >
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{fmt(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => openDetails(u.id)}
                          className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/80 text-xs"
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          disabled={isSelf}
                          onClick={() => patchUser(u.id, { role: u.role === "user" ? "admin" : "user" })}
                          className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/80 text-xs disabled:opacity-30"
                        >
                          {u.role === "user" ? "Make admin" : "Make user"}
                        </button>
                        <button
                          type="button"
                          disabled={isSelf}
                          onClick={() =>
                            patchUser(u.id, { status: u.status === "active" ? "suspended" : "active" })
                          }
                          className={`px-2 py-1 rounded text-xs disabled:opacity-30 ${
                            u.status === "active"
                              ? "bg-red-500/15 hover:bg-red-500/25 text-red-300"
                              : "bg-green-500/15 hover:bg-green-500/25 text-green-300"
                          }`}
                        >
                          {u.status === "active" ? "Disable" : "Enable"}
                        </button>
                        <button
                          type="button"
                          disabled={isSelf || isAdmin || removingId === u.id}
                          title={
                            isSelf
                              ? "You can't remove your own account"
                              : isAdmin
                                ? "Demote to user before removing"
                                : "Remove this user"
                          }
                          onClick={() => removeUser(u)}
                          className="px-2 py-1 rounded bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs disabled:opacity-30"
                        >
                          {removingId === u.id ? "Removing…" : "Remove"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {activeUsers.length === 0 && !busy && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-white/40">
                    No users.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {removedUsers.length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-1">
              Removed accounts <span className="text-white/30">({removedUsers.length})</span>
            </h2>
            <p className="text-xs text-white/40 mb-3">
              These accounts can't sign in and their email can't re-register. Restore to bring one
              back, or Delete permanently to erase it and free the email for a new account.
            </p>
            <div className="glass overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white/50 text-left">
                  <tr className="border-b border-white/10">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {removedUsers.map((u) => (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 opacity-80">
                      <td className="px-4 py-3">
                        <div className="text-white/80 font-medium line-through decoration-white/30">
                          {u.display_name}
                        </div>
                        <div className="text-white/40 text-xs">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/50">
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/50 text-xs">{fmt(u.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => patchUser(u.id, { status: "active" })}
                            className="px-2 py-1 rounded bg-green-500/15 hover:bg-green-500/25 text-green-300 text-xs"
                          >
                            Restore
                          </button>
                          <button
                            type="button"
                            disabled={purgingId === u.id}
                            onClick={() => purgeUser(u)}
                            className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs disabled:opacity-30"
                          >
                            {purgingId === u.id ? "Deleting…" : "Delete permanently"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
      )}

      {tab === "devices" && (
        <div className="glass overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-white/50 text-left">
              <tr className="border-b border-white/10">
                <th className="px-4 py-3">Machine</th>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">OS</th>
                <th className="px-4 py-3">Presence</th>
                <th className="px-4 py-3">Users</th>
                <th className="px-4 py-3">Registered</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3 text-white font-medium">{d.hostname}</td>
                  <td className="px-4 py-3 font-mono text-white/70">{d.access_code ?? "—"}</td>
                  <td className="px-4 py-3 text-white/60">{d.os}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 ${d.online ? "text-green-400" : "text-white/40"}`}>
                      <span className={`w-2 h-2 rounded-full ${d.online ? "bg-green-400" : "bg-white/30"}`} />
                      {d.online ? "Online" : "Offline"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/60">{d.claimants}</td>
                  <td className="px-4 py-3 text-white/50 text-xs">{fmt(d.created_at)}</td>
                </tr>
              ))}
              {devices.length === 0 && !busy && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-white/40">
                    No machines registered.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "sessions" && (
        <>
          <div className="flex gap-2 mb-3">
            {(["active", "all"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSessionScope(s)}
                className={`px-3 py-1.5 rounded-lg text-xs ${
                  sessionScope === s ? "bg-accent/30 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {s === "active" ? "Active" : "All recent"}
              </button>
            ))}
          </div>
          <div className="glass overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-left">
                <tr className="border-b border-white/10">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Machine</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const live = s.status === "active" || s.status === "pending";
                  return (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div className="text-white">{s.initiator_name}</div>
                        <div className="text-white/40 text-xs">{s.initiator_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white/80">{s.device_hostname}</div>
                        <div className="text-white/40 text-xs font-mono">{s.device_code ?? "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-white/60">{s.mode}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            live ? "bg-blue-500/20 text-blue-300" : "bg-white/10 text-white/60"
                          }`}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/50 text-xs">{fmt(s.started_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          disabled={!live}
                          onClick={() => terminate(s.id)}
                          className="px-2 py-1 rounded bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs disabled:opacity-30"
                        >
                          Terminate
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {sessions.length === 0 && !busy && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-white/40">
                      No {sessionScope === "active" ? "active" : ""} connections.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {detailId && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4 sm:p-8"
          onMouseDown={(e) => {
            backdropDown.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && backdropDown.current) setDetailId(null);
          }}
        >
          <div
            className="glass w-full max-w-3xl my-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">User details</h2>
                <p className="text-xs text-white/40">Full account, machines and recent connections.</p>
              </div>
              <button
                type="button"
                onClick={() => setDetailId(null)}
                className="px-2 py-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 text-lg leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {detailBusy && <div className="text-white/40 text-sm">Loading…</div>}
            {detailErr && (
              <div className="mb-3 px-4 py-2 rounded-lg bg-red-500/10 text-red-300 text-sm">
                {detailErr}
              </div>
            )}

            {detail && (
              <>
                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  <div className="rounded-lg bg-white/5 px-4 py-3">
                    <div className="text-white text-lg font-semibold">{detail.user.display_name}</div>
                    <div className="text-white/50 text-sm">{detail.user.email}</div>
                    <div className="flex gap-2 mt-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          detail.user.role === "admin" || detail.user.role === "owner"
                            ? "bg-purple-500/20 text-purple-200"
                            : "bg-white/10 text-white/70"
                        }`}
                      >
                        {detail.user.role}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          detail.user.status === "active"
                            ? "bg-green-500/20 text-green-300"
                            : "bg-red-500/20 text-red-300"
                        }`}
                      >
                        {detail.user.status}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/5 px-4 py-3 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-white/50">Machines owned</span>
                      <span className="text-white font-medium">{detail.counts.devices}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">Connections started</span>
                      <span className="text-white font-medium">{detail.counts.sessions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">Joined</span>
                      <span className="text-white/70">{fmt(detail.user.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">User ID</span>
                      <span className="text-white/40 font-mono text-xs">{detail.user.id.slice(0, 8)}…</span>
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <div className="text-white/60 text-sm font-medium mb-2">
                    Machines ({detail.devices.length})
                  </div>
                  {detail.devices.length === 0 ? (
                    <div className="text-white/40 text-sm">No machines owned.</div>
                  ) : (
                    <div className="rounded-lg overflow-hidden border border-white/10">
                      <table className="w-full text-sm">
                        <thead className="text-white/40 text-left bg-white/5">
                          <tr>
                            <th className="px-3 py-2 font-medium">Machine</th>
                            <th className="px-3 py-2 font-medium">Code</th>
                            <th className="px-3 py-2 font-medium">OS</th>
                            <th className="px-3 py-2 font-medium">Users</th>
                            <th className="px-3 py-2 font-medium">Registered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.devices.map((d) => (
                            <tr key={d.id} className="border-t border-white/5">
                              <td className="px-3 py-2 text-white">{d.hostname}</td>
                              <td className="px-3 py-2 font-mono text-white/70">{d.access_code ?? "—"}</td>
                              <td className="px-3 py-2 text-white/50">{d.os}</td>
                              <td className="px-3 py-2 text-white/50">{d.claimants}</td>
                              <td className="px-3 py-2 text-white/40 text-xs">{fmt(d.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <div className="text-white/60 text-sm font-medium mb-2">
                    Recent connections ({detail.sessions.length})
                  </div>
                  {detail.sessions.length === 0 ? (
                    <div className="text-white/40 text-sm">No connections started.</div>
                  ) : (
                    <div className="rounded-lg overflow-hidden border border-white/10 max-h-56 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="text-white/40 text-left bg-white/5 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 font-medium">Machine</th>
                            <th className="px-3 py-2 font-medium">Mode</th>
                            <th className="px-3 py-2 font-medium">Status</th>
                            <th className="px-3 py-2 font-medium">Started</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.sessions.map((s) => (
                            <tr key={s.id} className="border-t border-white/5">
                              <td className="px-3 py-2 text-white/80">{s.device_hostname}</td>
                              <td className="px-3 py-2 text-white/50">{s.mode}</td>
                              <td className="px-3 py-2 text-white/50">{s.status}</td>
                              <td className="px-3 py-2 text-white/40 text-xs">{fmt(s.started_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 justify-end border-t border-white/10 pt-4">
                  <button
                    type="button"
                    disabled={detail.user.id === me?.id}
                    onClick={async () => {
                      const m = await patchUser(detail.user.id, {
                        role: detail.user.role === "user" ? "admin" : "user"
                      });
                      if (m) setDetailErr(m);
                      else openDetails(detail.user.id);
                    }}
                    className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-sm disabled:opacity-30"
                  >
                    {detail.user.role === "user" ? "Make admin" : "Make user"}
                  </button>
                  <button
                    type="button"
                    disabled={detail.user.id === me?.id}
                    onClick={async () => {
                      const m = await patchUser(detail.user.id, {
                        status: detail.user.status === "active" ? "suspended" : "active"
                      });
                      if (m) setDetailErr(m);
                      else openDetails(detail.user.id);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm disabled:opacity-30 ${
                      detail.user.status === "active"
                        ? "bg-red-500/15 hover:bg-red-500/25 text-red-300"
                        : "bg-green-500/15 hover:bg-green-500/25 text-green-300"
                    }`}
                  >
                    {detail.user.status === "active" ? "Disable" : "Enable"}
                  </button>
                  <button
                    type="button"
                    disabled={
                      detail.user.id === me?.id ||
                      detail.user.role === "admin" ||
                      detail.user.role === "owner" ||
                      removingId === detail.user.id
                    }
                    title={
                      detail.user.role === "admin" || detail.user.role === "owner"
                        ? "Demote to user before removing"
                        : "Remove this user"
                    }
                    onClick={async () => {
                      const m = await removeUser(detail.user);
                      if (m) setDetailErr(m);
                    }}
                    className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm disabled:opacity-30"
                  >
                    {removingId === detail.user.id ? "Removing…" : "Remove user"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
